import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import type { BankDetailsUpdateInterface } from '@zaoblako/shared/modules/client/dtos/bank.details/bank.details.update.dto';
import { CrmTableColumn } from '@zaoblako/web/core/models/crm-table-column.interface';
import { TypeInterface } from '@zaoblako/web/core/models/type.interface';
import type { BankFindInterface } from '@zaoblako/shared/modules/catalog/dtos/bank/bank-find.dto';
import { JurisdictionTypeEnum } from '@zaoblako/shared/modules/client/enums/jurisdiction-type.enum';
import { BankAutoCompleteComponent } from '@zaoblako/web/shared/dumbs/autoComplete/bank-auto-complete/bank-auto-complete.component';
import { InputTextComponent } from '@zaoblako/web/shared/dumbs/input-text/input-text.component';
import { TableComponent } from '@zaoblako/web/shared/dumbs/table/table.component';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import { ButtonComponent } from '@zaoblako/web/shared/dumbs/button/button.component';
import { CardModule } from 'primeng/card';
import { CheckboxModule } from 'primeng/checkbox';

@Component({
  standalone: true,
  selector: 'crm-table-bank-details',
  templateUrl: './table-bank-details.component.html',
  imports: [
    CommonModule,
    CardModule,
    BankAutoCompleteComponent,
    InputTextComponent,
    ButtonComponent,
    JoinPipe,
    TableComponent,
    CheckboxModule,
    FormsModule
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TableBankDetailsComponent implements OnChanges, OnInit {
  @Input() public bankDetails: BankDetailsUpdateInterface[] = [];
  @Output() public bankDetailsChange = new EventEmitter<BankDetailsUpdateInterface[]>();

  @Input() public banks: BankFindInterface[] = [];
  @Input() public header = 'Расчетные счета';
  @Input() public jurisdiction: JurisdictionTypeEnum = null;

  @Input() public withCard = true;
  @Input() public showDefaultBankDetail = false;
  @Input() public showPaymentsViaBank = false;

  @Input() public errors: any[];
  public typeErrors: TypeInterface;

  @Output() public bankSearch = new EventEmitter<string>();

  public get JurisdictionTypeEnum() {
    return JurisdictionTypeEnum;
  }

  public columns: CrmTableColumn[] = [{ label: 'Название' }, { label: 'Банк' }, { label: 'Расчетный счёт' }, {}];

  public ngOnInit(): void {
    const extraColumns: CrmTableColumn[] = [];
    if (this.showDefaultBankDetail) {
      extraColumns.push({ label: 'Счёт по умолчанию' });
    }
    if (this.showPaymentsViaBank) {
      extraColumns.push({ label: 'Платежи через банк' });
    }

    this.columns = [
      { label: 'Название' },
      { label: 'Банк' },
      { label: 'Расчетный счёт' },
      ...extraColumns,
      {}
    ];

    if (this.jurisdiction !== JurisdictionTypeEnum.FOREIGN && !this.columns.find((column) => column.label === 'Банк')) {
      this.columns.splice(1, 0, { label: 'Банк' });
      this.columns = [...this.columns];
    }

    if (this.jurisdiction === JurisdictionTypeEnum.FOREIGN && this.columns.find((column) => column.label === 'Банк')) {
      this.columns = this.columns.filter((column) => column.label !== 'Банк');
    }
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.errors?.currentValue) {
      this.typeErrors = typeof this.errors;
    }
    if (changes.jurisdiction?.currentValue) {
      if (
        changes.jurisdiction?.currentValue === JurisdictionTypeEnum.FOREIGN &&
        this.columns.find((column) => column.label === 'Банк')
      ) {
        this.columns = this.columns.filter((column) => column.label !== 'Банк');
      }

      if (
        changes.jurisdiction?.currentValue !== JurisdictionTypeEnum.FOREIGN &&
        !this.columns.find((column) => column.label === 'Банк')
      ) {
        this.columns.splice(1, 0, { label: 'Банк' });
        this.columns = [...this.columns];
      }
    }
  }

  public toBankDetail(bankDetail: any): BankDetailsUpdateInterface {
    return bankDetail as BankDetailsUpdateInterface;
  }

  public addBankDetail() {
    if (!this.bankDetails) {
      this.bankDetails = [];
    }
    this.bankDetails.push(<BankDetailsUpdateInterface>{});
    this.bankDetails = [...this.bankDetails];
    this.bankDetailsChange.emit(this.bankDetails);
  }

  public deleteBankDetail(bankDetail: BankDetailsUpdateInterface) {
    this.bankDetails = this.bankDetails.filter((_bankDetail) => _bankDetail !== bankDetail);
    this.bankDetailsChange.emit(this.bankDetails);
  }

  public setDefaultBankDetail(index: number, value: boolean) {
    this.bankDetails = this.bankDetails.map((detail) => ({ ...detail, isDefault: false }));
    this.bankDetails[index].isDefault = value;
    this.bankDetails = [...this.bankDetails];
    this.bankDetailsChange.emit(this.bankDetails);
  }
}
